/**
 * Custom Plan Modal Handler
 * Handles modal display and form submission for custom plan requests
 */

function initCustomPlanModal() {
  const modalTriggers = document.querySelectorAll('[data-modal-trigger="custom-plan-modal"]');
  const modal = document.getElementById('custom-plan-modal');
  const closeButtons = document.querySelectorAll('[data-modal-close]');
  const form = document.getElementById('custom-plan-form');

  if (!modal || !form) {
    return;
  }

  // Open modal when trigger is clicked
  modalTriggers.forEach(trigger => {
    trigger.addEventListener('click', function(e) {
      e.preventDefault();
      // Store the plan type from the trigger element
      const planType = this.getAttribute('data-plan-type') || 'standard';
      modal.setAttribute('data-selected-plan-type', planType);
      openModal(modal);
    });
  });

  // Close modal when close buttons are clicked (using event delegation)
  document.addEventListener('click', function(e) {
    if (e.target.matches('[data-modal-close]')) {
      console.log('close modal');
      closeModal(modal);
    }
  });

  // Close modal when clicking outside
  modal.addEventListener('click', function(e) {
    if (e.target === modal) {
      closeModal(modal);
    }
  });

  // Close modal with Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal.classList.contains('modal--active')) {
      closeModal(modal);
    }

    // Prevent "0" from being typed in number_of_voters field
    const activeElement = document.activeElement;
    if (e.key == '0' && activeElement && activeElement.name === 'number_of_voters' && activeElement.value.length === 0) {
      e.preventDefault(); // prevent "0" as the first character
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && modal.classList.contains('modal--active')) {
      e.preventDefault();
      handleFormSubmission(form, modal);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal.classList.contains('modal--active')) {
      closeModal(modal);
    }
  });

  // Handle form submission
  form.addEventListener('submit', function(e) {
    e.preventDefault();
    handleFormSubmission(form, modal);
  });

  // Initialize form validation
  initCustomPlanFormValidation(form);
}

/**
 * Open modal with animation
 */
function openModal(modal) {
  modal.classList.add('modal--active');
  document.body.style.overflow = 'hidden';

  // Focus first input
  const firstInput = modal.querySelector('input[required]');
  if (firstInput) {
    setTimeout(() => firstInput.focus(), 100);
  }
}

/**
 * Close modal with animation
 */
function closeModal(modal) {
  console.log('close modal');
  modal.classList.remove('modal--active');
  document.body.style.overflow = '';
  
  const modalBody = modal.querySelector('.modal__body');
  if (modal.dataset.formHtml) {
    modalBody.innerHTML = modal.dataset.formHtml;
    delete modal.dataset.formHtml;

    // Gắn lại sự kiện submit và validation cho form sau khi khôi phục
    const form = modal.querySelector('form');
    if (form) {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        handleFormSubmission(form, modal);
      });
      initCustomPlanFormValidation(form);
    }

    // Hiện lại footer
    const modalFooter = modal.querySelector('.modal__footer');
    if (modalFooter) {
      modalFooter.style.display = 'flex';
    }
  }

  // Reset form
  const form = modal.querySelector('form');
  if (form) {
    form.reset();
    clearFormErrors(form);
  }
  
  // Clear selected plan type
  modal.removeAttribute('data-selected-plan-type');
}

/**
 * Handle form submission
 */
async function handleFormSubmission(form, modal) {
  const submitButton = modal.querySelector('button[type="submit"]');
  const formData = new FormData(form);

  // Validate form
  if (!validateCustomPlanForm(form)) {
    return;
  }

  // Show loading state
  setLoadingState(submitButton, true);

  try {
    // Get the selected plan type from modal
    const selectedPlanType = modal.getAttribute('data-selected-plan-type') || 'standard';
    console.log('Selected plan type:', selectedPlanType);

    // Prepare API payload
    const payload = {
      email: formData.get('email').trim().toLowerCase(),
      first_name: formData.get('first_name').trim(),
      last_name: formData.get('last_name').trim(),
      number_of_voters: parseInt(formData.get('number_of_voters')),
      notes: formData.get('notes')?.trim() || null,
      metadata: {
        source: 'website_form',
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        package_type: selectedPlanType
      }
    };

    // Make API call
    const response = await fetch(`${API_BASE_URL}/api/custom-plan-requests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (response.ok && result.success) {
      // Success
      showSuccessMessage(modal, result.data);
    } else {
      // Handle API errors
      handleApiError(result, form);
    }

  } catch (error) {
    console.error('Error submitting custom plan request:', error);
    
    // Get API base URL for debug info
    const apiBaseUrl = typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : (window.API_BASE_URL || 'https://api.electhq.net');
    
    // Use shared error modal utility
    if (typeof window.showErrorModal === 'function') {
      const formData = new FormData(form);
      await window.showErrorModal(error, `${apiBaseUrl}/api/custom-plan-requests`, {
        title: 'Custom Plan Request Error',
        message: 'Network error. Please try again later.',
        additionalInfo: {
          formType: 'custom-plan-modal',
          email: formData.get('email') || '',
        },
      });
    } else {
      // Fallback if utility not loaded
      showErrorMessage(modal, 'Network error. Please try again later.');
    }
  } finally {
    setLoadingState(submitButton, false);
  }
}

/**
 * Validate custom plan form
 */
function validateCustomPlanForm(form) {
  let isValid = true;
  clearFormErrors(form);

  const requiredFields = ['first_name', 'last_name', 'email', 'number_of_voters'];

  requiredFields.forEach(fieldName => {
    const field = form.querySelector(`[name="${fieldName}"]`);
    const value = field.value.trim();

    if (!value) {
      showFieldError(field, `${getFieldLabel(fieldName)} is required`);
      isValid = false;
    } else if (fieldName === 'email' && !isValidEmail(value)) {
      showFieldError(field, 'Please enter a valid email address');
      isValid = false;
    } else if (fieldName === 'number_of_voters') {
      const numVoters = parseInt(value);
      if (isNaN(numVoters) || numVoters <= 0) {
        showFieldError(field, 'Number of voters must be a positive number');
        isValid = false;
      }
    }
  });

  return isValid;
}

/**
 * Initialize form validation with real-time feedback
 */
function initCustomPlanFormValidation(form) {
  const inputs = form.querySelectorAll('input, textarea');

  inputs.forEach(input => {
    input.addEventListener('blur', function() {
      validateField(this);
    });

    input.addEventListener('input', function() {
      // Clear error on input
      clearFieldError(this);
    });
  });
}

/**
 * Validate individual field
 */
function validateField(field) {
  const value = field.value.trim();
  const fieldName = field.name;

  clearFieldError(field);

  if (field.hasAttribute('required') && !value) {
    showFieldError(field, `${getFieldLabel(fieldName)} is required`);
    return false;
  }

  if (fieldName === 'email' && value && !isValidEmail(value)) {
    showFieldError(field, 'Please enter a valid email address');
    return false;
  }

  if (fieldName === 'number_of_voters' && value) {
    const numVoters = parseInt(value);
    if (isNaN(numVoters) || numVoters <= 0) {
      showFieldError(field, 'Number of voters must be a positive number');
      return false;
    }
  }

  return true;
}

/**
 * Show field error
 */
function showFieldError(field, message) {
  field.classList.add('form-control--invalid');
  const errorElement = document.getElementById(`${field.name}_error`);
  if (errorElement) {
    errorElement.textContent = message;
    errorElement.classList.add('show');
  }
}

/**
 * Clear field error
 */
function clearFieldError(field) {
  field.classList.remove('form-control--invalid');
  const errorElement = document.getElementById(`${field.name}_error`);
  if (errorElement) {
    errorElement.textContent = '';
    errorElement.classList.remove('show');
  }
}

/**
 * Clear all form errors
 */
function clearFormErrors(form) {
  const errorElements = form.querySelectorAll('.form-error');
  const invalidFields = form.querySelectorAll('.form-control--invalid');

  errorElements.forEach(error => {
    error.textContent = '';
    error.classList.remove('show');
  });

  invalidFields.forEach(field => {
    field.classList.remove('form-control--invalid');
  });
}

/**
 * Get field label for error messages
 */
function getFieldLabel(fieldName) {
  const labels = {
    'first_name': 'First Name',
    'last_name': 'Last Name',
    'email': 'Work Email',
    'phone': 'Phone number',
    'number_of_voters': 'Number of voters',
    'notes': 'Notes'
  };
  return labels[fieldName] || fieldName;
}

/**
 * Validate email format
 */
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Set loading state for button
 */
function setLoadingState(button, isLoading) {
  if (isLoading) {
    button.classList.add('loading');
    button.disabled = true;
  } else {
    button.classList.remove('loading');
    button.disabled = false;
  }
}

/**
 * Show success message
 */
function showSuccessMessage(modal, data) {
  const modalBody = modal.querySelector('.modal__body');
  const form = modal.querySelector('form');

  modal.dataset.formHtml = modalBody.innerHTML;

  modalBody.innerHTML = `
    <div class="success-message">
      <div class="success-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#28a745" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22,4 12,14.01 9,11.01"></polyline>
        </svg>
      </div>
      <h3>Thank you for your request!</h3>
      <p>Your custom plan request has been submitted successfully. We'll review your requirements and contact you within 24 hours.</p>
      <div class="success-details">
        <p><strong>Request ID:</strong> #${data.id}</p>
        <p><strong>Email:</strong> ${data.email}</p>
        <p><strong>Voters:</strong> ${data.number_of_voters.toLocaleString()}</p>
      </div>
      <button type="button" class="btn btn_black" data-modal-close>Close</button>
    </div>
  `;
   // Hide the modal footer
   const modalFooter = modal.querySelector('.modal__footer');
   if (modalFooter) {
    modalFooter.style.display = 'none';
  }

  
  // Add success message styles
  addSuccessMessageStyles();

  // setTimeout(() => {
  //   modalBody.innerHTML = '';
  //   form.reset();
  //   modalFooter.style.display = 'flex';
  //   modalBody.appendChild(form);
  //   closeModal(modal);
  // }, 3000);
}

/**
 * Show error message
 */
function showErrorMessage(modal, message) {
  const modalBody = modal.querySelector('.modal__body');
  const form = modal.querySelector('form');

  modalBody.innerHTML = `
    <div class="error-message">
      <div class="error-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc3545" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      </div>
      <h3>Error</h3>
      <p>${message}</p>
      <button type="button" class="btn btn--primary" onclick="location.reload()">Try Again</button>
    </div>
  `;

  // Add error message styles
  addErrorMessageStyles();
}

/**
 * Handle API errors
 */
function handleApiError(result, form) {
  if (result.errors && Array.isArray(result.errors)) {
    // Handle validation errors
    result.errors.forEach(error => {
      const field = form.querySelector(`[name="${error.path}"]`);
      if (field) {
        showFieldError(field, error.msg);
      }
    });
  } else {
    // Handle other errors
    showErrorMessage(document.getElementById('custom-plan-modal'), result.message || 'An error occurred. Please try again.');
  }
}

/**
 * Add success message styles
 */
function addSuccessMessageStyles() {
  const style = document.createElement('style');
  style.textContent = `
    .success-message {
      text-align: center;
      padding: 2rem 0;
    }
    
    .success-icon {
      margin-bottom: 1rem;
    }
    
    .success-message h3 {
      color: #28a745;
      font-size: 1.5rem;
      margin-bottom: 1rem;
    }
    
    .success-message p {
      color: #6c757d;
      margin-bottom: 1.5rem;
      line-height: 1.6;
    }
    
    .success-details {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1.5rem;
      text-align: left;
    }
    
    .success-details p {
      margin: 0.5rem 0;
      font-size: 0.9rem;
    }
  `;
  document.head.appendChild(style);
}

/**
 * Add error message styles
 */
function addErrorMessageStyles() {
  const style = document.createElement('style');
  style.textContent = `
    .error-message {
      text-align: center;
      padding: 2rem 0;
    }
    
    .error-icon {
      margin-bottom: 1rem;
    }
    
    .error-message h3 {
      color: #dc3545;
      font-size: 1.5rem;
      margin-bottom: 1rem;
    }
    
    .error-message p {
      color: #6c757d;
      margin-bottom: 1.5rem;
      line-height: 1.6;
    }
  `;
  document.head.appendChild(style);
}

// Expose functions to global scope
window.initCustomPlanModal = initCustomPlanModal;